// CardBrowserFilterRow — Filterleiste der Kartenbestand-Suche. Dasselbe Vokabular wie die
// Sammlung, aber ohne „Quelle" (gesucht wird über den gesamten Kartenbestand, nicht über
// Binder/Decks). Alle Konsumenten filtern denselben Endpunkt mit demselben Filter-Objekt —
// ein neuer Filter entsteht deshalb genau einmal, hier.
import React, { useCallback, useMemo } from 'react';
import FilterStrip from '../../../ui/FilterStrip';
import SyntaxHelpPopover from '../../../ui/SyntaxHelpPopover';
import { manaColors, typeOptions } from '../../../components/uiDefinitions';
import { CARD_SORT_OPTIONS, naturalDirection } from '../../../utils/sortOptions';
import ManaW from '../../../assets/mana/W.svg';
import ManaU from '../../../assets/mana/U.svg';
import ManaB from '../../../assets/mana/B.svg';
import ManaR from '../../../assets/mana/R.svg';
import ManaG from '../../../assets/mana/G.svg';
import ManaC from '../../../assets/mana/C.svg';

const MANA_FILTER_COLORS = manaColors.map((c) => ({
  code: c.code,
  name: c.name,
  iconSrc: { W: ManaW, U: ManaU, B: ManaB, R: ManaR, G: ManaG, C: ManaC }[c.code],
}));

const TYPE_MENU_OPTIONS = typeOptions.map((t) => ({
  value: t,
  label: t.charAt(0).toUpperCase() + t.slice(1),
}));

const GROUP_MENU_OPTIONS = [
  { value: 'oracle', label: 'Eine Karte je Name' },
  { value: 'illustration', label: 'Ein Eintrag je Motiv' },
];

const OWNED_MENU_OPTIONS = [
  { value: 'all', label: 'Alle Karten' },
  { value: 'owned', label: 'Nur meine Sammlung' },
];

/**
 * @param {object} filters — Filter-Objekt in der Form von `EMPTY_CARD_BROWSER_FILTERS`
 * @param {(patch: object) => void} onChange — Teil-Änderung des Filter-Objekts
 * @param {boolean} showSort — Sortier-Gruppe zeigen; Ansichten mit eigener Reihenfolge
 *   (Rangliste statt Sortierung) schalten sie ab
 * @param {{value: string, label: string}[]} sortOptions — Sortier-Liste; der Katalog reicht
 *   die um den EDHREC-Rang erweiterte Liste herein
 * @param {boolean} showOwnedOnly — Umschalter „Nur meine Sammlung"
 * @param {boolean} showGrouping — Gruppierung (eine Karte je Name / je Motiv)
 * @param {boolean} mobileDock — Dock-Zustand der Leiste am Handy; eingebettete Flächen
 *   (Katalog-Spalte) schalten ihn ab, siehe JSDoc von `ui/FilterStrip`
 * @param {React.ReactNode} dockBar — zusätzliche Dock-Zeile (mobile Kandidaten-Leiste)
 */
export default function CardBrowserFilterRow({
  filters,
  onChange,
  showSort = true,
  sortOptions = CARD_SORT_OPTIONS,
  showOwnedOnly = false,
  showGrouping = false,
  mobileDock = true,
  dockBar = null,
}) {
  const toggleColor = useCallback((code) => {
    const current = filters.colors || [];
    onChange({
      colors: current.includes(code) ? current.filter((c) => c !== code) : [...current, code],
    });
  }, [filters.colors, onChange]);

  const activeExtraCount = useMemo(
    () => [filters.set, filters.keywords, filters.subtypes].filter(Boolean).length,
    [filters.set, filters.keywords, filters.subtypes]
  );

  return (
    <FilterStrip activeExtraCount={activeExtraCount} mobileDock={mobileDock}>
      <FilterStrip.Search
        value={filters.search}
        onChange={(value) => onChange({ search: value })}
        placeholder="Name / Syntax…"
        help={<SyntaxHelpPopover />}
      />
      <FilterStrip.Item id="farbe" active={(filters.colors || []).length > 0}>
        <FilterStrip.ManaColors
          colors={MANA_FILTER_COLORS}
          selected={filters.colors}
          onToggle={toggleColor}
        />
      </FilterStrip.Item>
      <FilterStrip.Item id="typen" active={(filters.types || []).length > 0}>
        <FilterStrip.Group label="Typen">
          <FilterStrip.PillMenu
            mode="multi"
            placeholder="Alle"
            options={TYPE_MENU_OPTIONS}
            value={filters.types}
            onChange={(value) => onChange({ types: value })}
            onClear={() => onChange({ types: [] })}
          />
        </FilterStrip.Group>
      </FilterStrip.Item>
      {showOwnedOnly && (
        <FilterStrip.Item id="sammlung" active={filters.ownedOnly === true}>
          <FilterStrip.Group label="Bestand">
            <FilterStrip.PillMenu
              mode="single"
              options={OWNED_MENU_OPTIONS}
              value={filters.ownedOnly ? 'owned' : 'all'}
              active={filters.ownedOnly === true}
              onChange={(value) => onChange({ ownedOnly: value === 'owned' })}
            />
          </FilterStrip.Group>
        </FilterStrip.Item>
      )}
      <FilterStrip.Item id="regeltext" active={Boolean(filters.oracleText)}>
        <FilterStrip.Group label="Regeltext">
          <FilterStrip.TextInput
            value={filters.oracleText}
            onChange={(value) => onChange({ oracleText: value })}
            placeholder="z. B. draw"
            width={140}
          />
        </FilterStrip.Group>
      </FilterStrip.Item>
      {(showSort || showGrouping) && (
        <FilterStrip.RightGroup>
          {showSort && (
            <FilterStrip.Item id="sortieren">
              <FilterStrip.Group label="Sortieren">
                <FilterStrip.PillMenu
                  mode="single"
                  options={sortOptions}
                  value={filters.sortOption}
                  onChange={(value) => onChange({ sortOption: value, sortDirection: naturalDirection(value) })}
                />
                <FilterStrip.SortDirection
                  direction={filters.sortDirection}
                  onClick={() => onChange({ sortDirection: filters.sortDirection === 'desc' ? 'asc' : 'desc' })}
                />
              </FilterStrip.Group>
            </FilterStrip.Item>
          )}
          {showGrouping && (
            <FilterStrip.Item id="gruppierung">
              <FilterStrip.Group label="Gruppierung">
                <FilterStrip.PillMenu
                  mode="single"
                  options={GROUP_MENU_OPTIONS}
                  value={filters.groupBy}
                  onChange={(value) => onChange({ groupBy: value })}
                />
              </FilterStrip.Group>
            </FilterStrip.Item>
          )}
        </FilterStrip.RightGroup>
      )}
      <FilterStrip.Extra>
        <FilterStrip.Group label="Set">
          <FilterStrip.TextInput
            value={filters.set}
            onChange={(value) => onChange({ set: value })}
            placeholder="z. B. DSK, MKM"
          />
        </FilterStrip.Group>
        <FilterStrip.Group label="Keywords">
          <FilterStrip.TextInput
            value={filters.keywords}
            onChange={(value) => onChange({ keywords: value })}
            placeholder="z. B. flying, trample"
          />
        </FilterStrip.Group>
        <FilterStrip.Group label="Subtypen">
          <FilterStrip.TextInput
            value={filters.subtypes}
            onChange={(value) => onChange({ subtypes: value })}
            placeholder="z. B. Elf, Goblin"
          />
        </FilterStrip.Group>
      </FilterStrip.Extra>
      {dockBar ? <FilterStrip.DockBar>{dockBar}</FilterStrip.DockBar> : null}
    </FilterStrip>
  );
}
